'use client';

import { runtimeEnv } from 'config/runtimeEnv';
import { useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GoogleMap, useLoadScript } from '@react-google-maps/api';
import DriverMarker from './DriverMarker';
import InfoWindowComponent from './InfoWindowComponent';
import useLiveDrivers from './UseLiveDrivers';
import {
  GET_DRIVER_REQUESTED,
  CLEAR_SELECTED_DRIVER,
  GET_DRIVER_LIST,
} from 'resources/actionTypes';
import LiveTrackingHeader from './LiveTrackingHeader';

const mapContainerStyle = {
  width: '100%',
  height: '600px',
};

export default function LiveCabsMap() {
  const selectedLocation = useSelector((state) => state?.locationDropdown?.selectedLocation);

  const latitude = selectedLocation?.geoPoint?.coordinates[0];
  const longitude = selectedLocation?.geoPoint?.coordinates[1];

  const initialCenter = useMemo(
    () => ({
      lat: latitude,
      lng: longitude,
    }),
    [latitude, longitude],
  );
  const locationId = selectedLocation?.id;
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: runtimeEnv('GOOGLE_API_KEY'),
  });

  const dispatch = useDispatch();
  const [selectDriverCoordinates, setSelectDriverCoordinates] = useState({
    driverId: '',
    lat: 0,
    lng: 0,
    status: '',
  });

  const [statusFilters, setStatusFilters] = useState({
    onDuty: true,
    onTrip: true,
    offDuty: true,
  });

  const [searchQuery, setSearchQuery] = useState('');

  // Get the selected driver details from Redux state
  const selectedDriver = useSelector((state) => state?.userDriver?.userDriver?.data?.body[0]);
  const loading = useSelector((state) => state?.loading);

  const drivers = useLiveDrivers(locationId);

  const filteredDrivers = useMemo(() => {
    if (!Array.isArray(drivers)) return [];

    return drivers.filter((driver) => {
      if (driver.status === 'onDuty' && !statusFilters.onDuty) return false;
      if (driver.status === 'onTrip' && !statusFilters.onTrip) return false;
      if (driver.status === 'offDuty' && !statusFilters.offDuty) return false;

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const vehicleNo = (driver.vehicleNo || '').toLowerCase();
        const tripShortId = (driver.tripShortId || '').toLowerCase();
        const driverMobile = (driver.driverMobile || '').toLowerCase();
        const driverName = (driver.driverName || '').toLowerCase();
        if (
          !vehicleNo.includes(query) &&
          !tripShortId.includes(query) &&
          !driverMobile.includes(query) &&
          !driverName.includes(query)
        )
          return false;
      }

      return true;
    });
  }, [drivers, statusFilters, searchQuery]);

  // Update selected driver coordinates dynamically
  const updatedSelectedDriver = useMemo(() => {
    if (!selectDriverCoordinates || !drivers) return null;
    return drivers.find((driver) => driver.driverId === selectDriverCoordinates.driverId) || null;
  }, [selectDriverCoordinates, drivers]);

  const handleDriverClick = (driver) => {
    // Reset the selected driver state to ensure InfoWindow opens again
    setSelectDriverCoordinates({
      driverId: driver.driverId,
      lat: driver.lat,
      lng: driver.lng,
      status: driver.status,
    });

    dispatch({
      type: GET_DRIVER_LIST.GET_DRIVER_LIST_REQUESTED,
      payload: { driverId: driver.driverId },
    });
  };

  const handleInfoWindowClose = () => {
    // Clear the selected driver state
    setSelectDriverCoordinates({
      driverId: '',
      lat: 0,
      lng: 0,
      status: '',
    });

    dispatch({ type: CLEAR_SELECTED_DRIVER });
    dispatch({
      type: 'CLEAR_SELECTED_TRIP',
    });
  };

  const [zoom, setZoom] = useState(12);
  const mapRef = useRef();

  const getDriverStatusSummary = (drivers) => {
    const summary = {
      onDuty: 0,
      offDuty: 0,
      onTrip: 0,
      total: 0,
    };

    drivers.forEach((driver) => {
      if (driver.status === 'onDuty') summary.onDuty += 1;
      else if (driver.status === 'offDuty') summary.offDuty += 1;
      else if (driver.status === 'onTrip') summary.onTrip += 1;
      summary.total += 1;
    });

    return summary;
  };

  const { onDuty, offDuty, onTrip, total } = getDriverStatusSummary(filteredDrivers);

  const onMapLoad = (map) => {
    mapRef.current = map;
    setZoom(map.getZoom());
  };

  const handleZoomChanged = () => {
    if (mapRef.current) {
      setZoom(mapRef.current.getZoom());
    }
  };

  const handleLocationChange = (locationId) => {
    console.log('Location changed:', locationId);
  };

  const mapOptions = {
    zoomControl: true, // Show zoom controls (+ and - icons)
    streetViewControl: false, // Show street view
    mapTypeControl: false, // Hide map/satellite toggle
    fullscreenControl: false, // Hide fullscreen button
    scaleControl: true, // Show scale control
    rotateControl: true, // Show rotate control
    panControl: true, // Show pan control
    scrollwheel: true, // Enable mouse scroll zoom
    gestureHandling: 'cooperative', // Enable default map movement
    cameraControl: false, // Disable camera controls
  };

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <div className="map-container">
      <LiveTrackingHeader
        handleLocationChange={handleLocationChange}
        total={total}
        onTrip={onTrip}
        onDuty={onDuty}
        offDuty={offDuty}
        statusFilters={statusFilters}
        setStatusFilters={setStatusFilters}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={zoom}
        center={initialCenter}
        onLoad={onMapLoad}
        onZoomChanged={handleZoomChanged}
        options={mapOptions}
      >
        {/* <ZoomControls zoom={zoom} setZoom={setZoom} mapRef={mapRef} /> */}

        {Array.isArray(filteredDrivers) &&
          filteredDrivers.map((driver) => (
            <DriverMarker
              key={driver.driverId}
              status={driver.status}
              position={{ lat: driver.lat, lng: driver.lng }}
              zoom={zoom}
              onClick={() => handleDriverClick(driver)} // Updated onClick
            />
          ))}
        {selectDriverCoordinates?.driverId !== '' && (
          <InfoWindowComponent
            key={updatedSelectedDriver.driverId} // ensure remount when driver changes
            position={{
              lat: updatedSelectedDriver.lat,
              lng: updatedSelectedDriver.lng,
            }}
            driver={
              selectedDriver
                ? {
                    ...selectedDriver,
                    status: updatedSelectedDriver.status,
                    tripId: updatedSelectedDriver.tripId || null,
                  }
                : null
            }
            loading={!selectedDriver}
            onClose={handleInfoWindowClose}
            updatedSelectedDriver={updatedSelectedDriver}
          />
        )}
      </GoogleMap>
    </div>
  );
}
